import { styled, useTheme } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { GraphData, LinkObject, NodeObject } from 'react-force-graph-2d';
import { Icon } from '@iconify/react';
import { FilterInput, FuseResult } from './FilterInput';
import { NodeItem, NodeItemActions } from './NodeItem';
import { NewNodesHeader } from './NewNodesHeader';
import { LinkItem } from './LinkItem';
import { NodeType } from '../Context';

export const drawerWidth = 400;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
}));

export interface DataDrawerProps extends NodeItemActions {
  data: GraphData;
  open: boolean;
  onClose?: () => void;
  isFullScreen: boolean;
  onLinkHover: (link: LinkObject) => void;
  onLinkLeave: (link: LinkObject) => void;
  onNodeChosen: (source: string, target: string, type: NodeType) => void;
  onRemoveNodeClick: (node: NodeObject) => void;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const ItemContainer: FC = ({ children }) => (
  <Stack sx={{ p: 2, maxWidth: drawerWidth }} spacing={1.5}>
    {children}
  </Stack>
);

export const DataDrawer: FC<DataDrawerProps> = ({
  data,
  open,
  onClose,
  isFullScreen,
  onNodeHover,
  onNodeLeave,
  onLinkHover,
  onLinkLeave,
  onNodeClick,
  onNodeChosen,
  onRemoveNodeClick,
}) => {
  const nodesRef = useRef(null);
  const linksRef = useRef(null);
  const theme = useTheme();

  const handleDrawerClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const [value, setValue] = useState(0);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    setValue(value);
  }, [data]);

  const scrollToNewNodes = () =>
    nodesRef?.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    if (nodesRef?.current) {
      scrollToNewNodes();
    }
  }, [data.nodes]);

  const scrollToNewLinks = () =>
    linksRef?.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    if (linksRef?.current) {
      scrollToNewLinks();
    }
  }, [data.links]);

  const [filteredData, setFilteredData] = useState<FuseResult<NodeObject>[]>();

  return (
    <Drawer
      sx={{
        flexShrink: 0,
        width: drawerWidth,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: 'rgba(8,12,30,0.98)',
          borderRight: '1px solid rgba(99,102,241,0.25)',
          color: '#e5e7eb',
        },
      }}
      variant="persistent"
      anchor="left"
      open={open}
    >
      <Box sx={{ mt: !isFullScreen ? 12 : 3 }} />
      <DrawerHeader>
        <Box
          sx={{
            width: drawerWidth - 16,
            position: 'fixed',
            backgroundColor: 'rgba(8,12,30,0.98)',
          }}
        >
          <FilterInput
            filterCallback={(nodeObjects) => {
              setFilteredData(nodeObjects);
            }}
            data={data.nodes}
          />
          {!filteredData && (
            <Tabs
              value={value}
              onChange={handleChange}
              sx={{
                '& .MuiTab-root': { color: '#9ca3af' },
                '& .MuiTab-root.Mui-selected': { color: '#c5cae9' },
                '& .MuiTabs-indicator': { backgroundColor: '#6366f1' },
              }}
            >
              <Tab label="Nodes" {...a11yProps(0)} />
              <Tab label="Links" {...a11yProps(1)} />
            </Tabs>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {!filteredData && (
          <Tooltip title={'Scroll to last nodes added'}>
            <Button
              onClick={() => {
                if (value === 0) {
                  scrollToNewNodes();
                } else {
                  scrollToNewLinks();
                }
              }}
              sx={{ color: '#6366f1' }}
            >
              New
            </Button>
          </Tooltip>
        )}
        <IconButton
          onClick={handleDrawerClose}
          sx={{ color: '#9ca3af', '&:hover': { color: '#c5cae9' } }}
        >
          {theme.direction === 'ltr' ? (
            <ChevronLeftIcon />
          ) : (
            <ChevronRightIcon />
          )}
        </IconButton>
      </DrawerHeader>
      <Divider sx={{ borderColor: 'rgba(99,102,241,0.2)' }} />
      <Box sx={{ overflow: 'auto' }}>
        {filteredData && (
          <>
            {filteredData.length === 0 ? (
              <Stack direction="row" justifyContent={'center'} sx={{ mt: 3 }}>
                <Stack alignItems={'center'}>
                  <Icon
                    icon="ic:sharp-data-object"
                    fontSize={50}
                    color={'#4e5054'}
                  />
                  <Typography variant={'h6'} sx={{ color: '#6b7280' }}>
                    No Results
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <ItemContainer>
                {filteredData.map((fuseResult) => (
                  <NodeItem
                    key={fuseResult.item.id}
                    nodes={data?.nodes}
                    node={fuseResult.item}
                    onNodeHover={onNodeHover}
                    onNodeLeave={onNodeLeave}
                    onNodeClick={onNodeClick}
                    onNodeChosen={onNodeChosen}
                    onRemoveNodeClick={onRemoveNodeClick}
                  />
                ))}
              </ItemContainer>
            )}
          </>
        )}
        {value === 0 && !filteredData && (
          <ItemContainer>
            {data?.nodes?.map((node, i) => (
              <>
                <NewNodesHeader
                  i={i}
                  text={'Last nodes added...'}
                  currentNodeOrLInk={node}
                  previousNodeOrLink={data?.nodes[i - 1]}
                  nodesRef={nodesRef}
                />
                <NodeItem
                  node={node}
                  nodes={data?.nodes}
                  onNodeHover={onNodeHover}
                  onNodeLeave={onNodeLeave}
                  onNodeClick={onNodeClick}
                  onNodeChosen={onNodeChosen}
                  onRemoveNodeClick={onRemoveNodeClick}
                />
              </>
            ))}
          </ItemContainer>
        )}
        {value === 1 && !filteredData && (
          <ItemContainer>
            {data?.links?.map((link, i) => (
              <>
                <NewNodesHeader
                  i={i}
                  text={'Last links added...'}
                  currentNodeOrLInk={link}
                  previousNodeOrLink={data?.links[i - 1]}
                  nodesRef={linksRef}
                />
                <LinkItem
                  link={link}
                  onLinkHover={onLinkHover}
                  onLinkLeave={onLinkLeave}
                />
              </>
            ))}
          </ItemContainer>
        )}
      </Box>
    </Drawer>
  );
};
